import { memo, useMemo } from 'react';
import PropTypes from 'prop-types';

import { colourForToken, formatValue, WidgetPeriodChip } from '../shared';

/**
 * The operator a formula's parts are joined by, as a reader writes it.
 *
 * Taken from `formula` rather than assumed: `won / total` is a ratio, but a
 * composite can just as well be `revenue - cost`. Printing `÷` between the
 * parts of a subtraction would be a lie the widget had no reason to tell.
 *
 * Unknown shapes fall back to a separator that claims nothing.
 */
const OPERATORS = Object.freeze({ '/': '÷', '*': '×', '-': '−', '+': '+' });

const operatorOf = (formula) => {
  const found = [...String(formula ?? '')].find((character) => character in OPERATORS);
  return found ? OPERATORS[found] : '·';
};

/**
 * One figure and the parts it was computed from — `COMPOSITE_METRIC`.
 *
 * ## Why this is not `StatRowWidget`
 *
 * It was, as a `divided` variant, and the two drew the same row of equal
 * boxes. That is right for `METRIC_ROW`, where the items are three
 * independent counters, and wrong here, where two of the three numbers **are**
 * the third. Rendered as peers, `71` and `250` read as metrics in their own
 * right rather than as the numerator and denominator of the 28.4% beside them.
 *
 * So: the result large and alone, then its parts on one line below, joined by
 * the formula's own operator. The hierarchy is the content — a reader who
 * takes in only the top line has still read the widget correctly.
 *
 * `formula` goes above as the widget's own small caption. It names what is
 * being computed (`won / total`), which is context for the figure, not a label
 * competing with it.
 *
 * Each part carries both its human `label` and its `alias` — the key the
 * author bound. The alias is what makes the number traceable back to the
 * query, and it is small and parenthesised because it is for the person
 * checking the widget, not the person reading it.
 *
 * The result takes the colour the backend's thresholds produced, as a dot.
 * The parts stay neutral: a part of a formula is not in a state of its own.
 *
 * @param {object} props
 * @param {object} props.data `COMPOSITE_METRIC`'s result and components.
 * @param {string} [props.className]
 */
function CompositeMetricWidget({ data, className = '' }) {
  const parts = useMemo(() => (data?.components ?? []).map((component, index) => ({
    key: component.alias ?? component.label ?? String(index),
    label: component.label ?? component.alias ?? '',
    alias: component.alias ?? '',
    /*
      A component prints with **its own** `format` or with none — never the
      result's. The result of `won / total` is a ratio and its parts are
      counts, so inheriting `{style: PERCENT}` turns 71 into «7,100.0%». The
      same breaks any composite whose result is a rate, a duration or a cost
      per unit.
    */
    value: formatValue(component.value, component.format),
  })), [data]);

  if (data?.value === undefined || data?.value === null) return null;

  const operator = operatorOf(data.formula);
  const dot = data.color_token ? colourForToken(data.color_token) : null;

  return (
    <div className={`flex min-w-0 flex-col gap-1.5 ${className}`.trim()}>
      {data.formula ? (
        <span className="truncate text-[10px] text-content-muted">{data.formula}</span>
      ) : null}

      <span className="flex items-center gap-2">
        {dot ? (
          <span
            aria-hidden
            className="h-2 w-2 shrink-0 rounded-full"
            style={{ background: dot }}
          />
        ) : null}
        <span className="text-2xl font-bold leading-none text-content">
          {formatValue(data.value, data.format)}
        </span>
      </span>

      {parts.length ? (
        <span className="flex min-w-0 flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
          {parts.map((part, index) => (
            <span key={part.key} className="flex items-baseline gap-1">
              {index > 0 ? (
                <span className="mr-0.5 text-[11px] text-content-faint" aria-hidden>
                  {operator}
                </span>
              ) : null}
              <span className="text-xs font-semibold text-content">{part.value}</span>
              <span className="text-[11px] text-content-muted">{part.label}</span>
              {part.alias && part.alias !== part.label ? (
                <span className="text-[10px] text-content-faint">({part.alias})</span>
              ) : null}
            </span>
          ))}
        </span>
      ) : null}

      <WidgetPeriodChip period={data.period} className="self-start" />
    </div>
  );
}

CompositeMetricWidget.propTypes = {
  data: PropTypes.shape({
    value: PropTypes.number,
    format: PropTypes.object,
    formula: PropTypes.string,
    components: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        alias: PropTypes.string,
        value: PropTypes.number,
        format: PropTypes.object,
      }),
    ),
    color_token: PropTypes.string,
    period: PropTypes.object,
  }).isRequired,
  className: PropTypes.string,
};

export default memo(CompositeMetricWidget);
